import React, { useEffect, useRef, useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../../data/entoridMock';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TestimonialsSection() {
  const scrollerRef = useRef(null);
  const [items, setItems] = useState(testimonials);

  useEffect(() => {
    let live = true;
    const load = () => axios.get(`${API}/api/public/homepage-testimonials`).then((res) => {
      if (live && Array.isArray(res.data?.items) && res.data.items.length) setItems(res.data.items);
    }).catch(() => {});
    load();
    const rotate = () => load();
    const timer = window.setInterval(rotate, 45000);
    window.addEventListener('focus', rotate);
    return () => { live = false; window.clearInterval(timer); window.removeEventListener('focus', rotate); };
  }, []);

  const scrollByCard = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector('[data-card]');
    const distance = card ? card.offsetWidth + 24 : 320;
    el.scrollBy({ left: direction * distance, behavior: 'smooth' });
  };

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="py-16 sm:py-20 lg:py-24 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between gap-4 mb-10 lg:mb-14">
          <div className="text-center lg:text-left mx-auto lg:mx-0 max-w-2xl">
            <span className="inline-block text-xs sm:text-sm font-semibold uppercase tracking-wider text-brand-green">Testimonials</span>
            <h2
              className="mt-2 text-2xl sm:text-3xl lg:text-4xl font-bold text-brand-blue"
              style={{ fontFamily: 'Poppins, Inter, sans-serif' }}
              data-testid="testimonials-title"
            >
              Loved by 10,000+ Customers
            </h2>
            <p className="mt-3 text-base text-gray-600">Real reviews from real homes across Uttarakhand.</p>
          </div>

          <div className="hidden lg:flex gap-2 shrink-0">
            <button
              type="button"
              onClick={() => scrollByCard(-1)}
              data-testid="testimonials-prev"
              aria-label="Previous testimonial"
              className="w-11 h-11 rounded-full bg-white ring-1 ring-gray-200 hover:ring-brand-green hover:text-brand-green text-gray-600 inline-flex items-center justify-center transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              onClick={() => scrollByCard(1)}
              data-testid="testimonials-next"
              aria-label="Next testimonial"
              className="w-11 h-11 rounded-full bg-white ring-1 ring-gray-200 hover:ring-brand-green hover:text-brand-green text-gray-600 inline-flex items-center justify-center transition-colors"
            >
              <ChevronRight size={20} />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-5 lg:gap-6 overflow-x-auto snap-x snap-mandatory pb-4 -mx-5 px-5 sm:mx-0 sm:px-0 scrollbar-thin"
          style={{ scrollbarWidth: 'thin' }}
          data-testid="testimonials-scroller"
        >
          {items.map((t) => (
            <article
              key={t.id}
              data-card
              data-testid={`testimonial-${t.id}`}
              className="snap-start shrink-0 w-[85%] sm:w-[60%] lg:w-[calc((100%-3rem)/3)] bg-white rounded-2xl p-6 sm:p-7 ring-1 ring-gray-100 shadow-sm hover:shadow-lg transition-shadow"
            >
              <Quote size={28} className="text-brand-green/30 mb-3" />
              <div className="flex gap-1 mb-3" aria-label={`${t.rating} out of 5 stars`}>
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mb-5 text-[15px]">"{t.text}"</p>
              <div className="flex items-center gap-3 pt-4 border-t border-gray-100">
                <img
                  src={t.image}
                  alt={t.name}
                  loading="lazy"
                  className="w-11 h-11 rounded-full object-cover ring-2 ring-brand-green/20"
                />
                <div>
                  <div className="font-semibold text-brand-blue text-sm">{t.name}</div>
                  <div className="text-xs text-gray-500">{t.location}</div>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
